<c-container fluid class="p-4">
    <c-row>
        <c-col>
            <!-- What the viewed node's own first line is currently worth. A bare
                 list of children says who is down there, not what it pays. -->
            @if (!loading() && summary(); as s) {
            <c-row class="g-3 mb-4">
                <c-col [sm]="6" [lg]="3">
                    <c-card class="h-100 shadow-sm border-0 summary-card">
                        <c-card-body>
                            <div class="summary-label">{{ 'user_tree.summary_active_directs' | translate }}</div>
                            <div class="summary-value">{{ s.active_direct_count }}</div>
                            <div class="summary-hint">
                                {{ 'user_tree.summary_direct_points' | translate }}: <strong>{{ s.direct_points }}</strong>
                                @if (s.to_next_direct_point !== null) {
                                <span class="ms-1 text-body-secondary">
                                    ({{ 'user_tree.summary_gap' | translate: { count: s.to_next_direct_point } }})
                                </span>
                                }
                            </div>
                        </c-card-body>
                    </c-card>
                </c-col>

                <c-col [sm]="6" [lg]="3">
                    <c-card class="h-100 shadow-sm border-0 summary-card">
                        <c-card-body>
                            <div class="summary-label">{{ 'user_tree.summary_pack_builders' | translate }}</div>
                            <div class="summary-value">{{ s.pack_builder_count }}</div>
                            <div class="summary-hint">
                                {{ 'user_tree.summary_builder_points' | translate }}: <strong>{{ s.pack_builder_points }}</strong>
                                @if (s.to_next_builder_bonus !== null) {
                                <span class="ms-1 text-body-secondary">
                                    ({{ 'user_tree.summary_gap' | translate: { count: s.to_next_builder_bonus } }})
                                </span>
                                }
                            </div>
                        </c-card-body>
                    </c-card>
                </c-col>

                <c-col [sm]="6" [lg]="3">
                    <c-card class="h-100 shadow-sm border-0 summary-card">
                        <c-card-body>
                            <div class="summary-label">{{ 'user_tree.summary_organization' | translate }}</div>
                            <div class="summary-value">{{ s.organization_volume }}</div>
                            <div class="summary-hint">
                                {{ 'user_tree.summary_downline' | translate:
                                    { active: s.downline_active, total: s.downline_total } }}
                            </div>
                        </c-card-body>
                    </c-card>
                </c-col>

                <c-col [sm]="6" [lg]="3">
                    <c-card class="h-100 shadow-sm border-0 summary-card">
                        <c-card-body>
                            <div class="summary-label">{{ 'user_tree.summary_rank' | translate }}</div>
                            <div class="summary-value">
                                {{ s.rank_name || ('user_tree.no_rank' | translate) }}
                            </div>
                            <div class="summary-hint">
                                @if (s.rank_name) {
                                <span class="me-2">{{ 'user_tree.weekly_bonus' | translate }}:
                                    <strong>{{ +s.weekly_bonus | number: '1.2-2' }}</strong></span>
                                }
                                @if (s.next_rank_name) {
                                <span class="text-body-secondary">
                                    {{ 'user_tree.summary_next_rank' | translate:
                                        { rank: s.next_rank_name, count: s.to_next_rank } }}
                                </span>
                                }
                            </div>
                        </c-card-body>
                    </c-card>
                </c-col>
            </c-row>
            }

            <c-card class="mb-4 shadow-sm border-0">
                <!-- Header + Breadcrumb -->
                <c-card-header class="p-0">
                    @if (breadcrumb().length > 0) {
                    <nav aria-label="breadcrumb" class="flex-fill">
                        <ol class="breadcrumb m-0 px-3 py-2">
                            @for (item of breadcrumb(); track item.user_id; let isLast = $last; let isFirst = $first) {
                            <li class="breadcrumb-item" [class.active]="isLast">
                                @if (!isLast) {
                                <a href="javascript:void(0)" (click)="navigateTo(item.user_id)"
                                    class="text-decoration-none text-primary">
                                    <svg cIcon name="cilUser" size="sm" class="me-1"></svg>
                                    {{ isFirst && item.user_id === null
                                    ? ('user_tree.root_users' | translate)
                                    : ('user_tree.user_id_label' | translate) + ' ' + item.user_id }}
                                </a>
                                } @else {
                                <span class="fw-semibold">
                                    <svg cIcon name="cilUser" size="sm" class="me-1"></svg>
                                    {{ isFirst && item.user_id === null
                                    ? ('user_tree.root_users' | translate)
                                    : ('user_tree.user_id_label' | translate) + ' ' + item.user_id }}
                                </span>
                                }
                            </li>
                            }
                        </ol>
                    </nav>
                    }
                </c-card-header>

                <c-card-body>
                    <!-- Loading -->
                    @if (loading()) {
                    <div class="text-center py-5">
                        <c-spinner color="primary" variant="grow"></c-spinner>
                        <p class="mt-3 text-body-secondary">{{ 'user_tree.loading' | translate }}</p>
                    </div>
                    }

                    <!-- Error -->
                    @if (error()) {
                    <div class="alert alert-danger alert-dismissible fade show" role="alert">
                        {{ error() }}
                        <button type="button" class="btn-close" (click)="error.set(null)"></button>
                    </div>
                    }

                    <!-- Tree Table -->
                    @if (!loading() && treeData(); as data) {
                    <!-- Below the PII depth the rows are reduced to bare ids; say so
                         rather than leaving the empty Name/E-Mail cells unexplained. -->
                    @if (!detailsVisible()) {
                    <div class="alert alert-info py-2 px-3 small mb-3" role="alert">
                        {{ 'user_tree.details_hidden_notice' | translate: { depth: data.details_visible_depth } }}
                    </div>
                    }

                    <div class="table-responsive">
                        <table cTable hover striped class="user-tree-table mb-0">
                            <thead>
                                <tr>
                                    <th class="sortable" (click)="sortBy('user_id')">
                                        {{ 'user_tree.column_user_id' | translate }}
                                        @if (getSortIcon('user_id'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    @if (detailsVisible()) {
                                    <th>{{ 'user_tree.column_contact' | translate }}</th>
                                    }
                                    <th class="sortable" (click)="sortBy('membership_status')">
                                        {{ 'user_tree.column_membership' | translate }}
                                        @if (getSortIcon('membership_status'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    <th class="text-center sortable" (click)="sortBy('active_direct_count')">
                                        {{ 'user_tree.column_active_directs' | translate }}
                                        @if (getSortIcon('active_direct_count'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    <th class="text-center sortable" (click)="sortBy('organization_volume')">
                                        {{ 'user_tree.column_organization' | translate }}
                                        @if (getSortIcon('organization_volume'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    <th class="text-center sortable" (click)="sortBy('volume_contribution')">
                                        {{ 'user_tree.column_contribution' | translate }}
                                        @if (getSortIcon('volume_contribution'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    <th class="sortable" (click)="sortBy('weekly_bonus')">
                                        {{ 'user_tree.column_rank' | translate }}
                                        @if (getSortIcon('weekly_bonus'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                    <th class="text-center sortable" (click)="sortBy('downline_active')">
                                        {{ 'user_tree.column_downline' | translate }}
                                        @if (getSortIcon('downline_active'); as icon) {
                                        <svg cIcon [name]="icon" size="sm" class="ms-1"></svg>
                                        }
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                @for (user of data.sponsored_users; track user.user_id) {
                                <tr [class.clickable]="user.has_sponsored_users"
                                    (click)="user.has_sponsored_users ? navigateTo(user.user_id) : null">
                                    <td>
                                        <span class="user-id fw-bold">{{ user.user_id }}</span>
                                        @if (user.founding_member) {
                                        <c-badge color="warning" shape="rounded-pill" class="ms-2"
                                            [title]="'user_tree.founding_member' | translate">FM</c-badge>
                                        }
                                        @if (user.has_sponsored_users) {
                                        <svg cIcon name="cilArrowRight" size="sm"
                                            class="ms-2 expand-icon text-primary"></svg>
                                        }
                                    </td>

                                    @if (detailsVisible()) {
                                    <td class="contact-cell">
                                        @if (user.details_visible) {
                                        <div class="fw-semibold">{{ user.name || '—' }}</div>
                                        <div class="small text-body-secondary">{{ user.email }}</div>
                                        @if (user.phone) {
                                        <div class="small text-body-secondary">{{ user.phone }}</div>
                                        }
                                        } @else {
                                        <span class="text-body-secondary">—</span>
                                        }
                                    </td>
                                    }

                                    <!-- The qualifying flag: everything else only pays
                                         while this membership is ACTIVE. -->
                                    <td>
                                        <c-badge [color]="membershipColor(user)" shape="rounded-pill">
                                            {{ membershipLabel(user) }}
                                        </c-badge>
                                        @if (user.plan_rhythm) {
                                        <div class="small text-body-secondary mt-1">
                                            {{ ('user_tree.rhythm_' + user.plan_rhythm) | translate }}
                                            @if (user.coverage_end_date) {
                                            · {{ user.coverage_end_date | date: 'shortDate' }}
                                            }
                                        </div>
                                        }
                                    </td>

                                    <td class="text-center">
                                        <span class="fw-medium">{{ user.active_direct_count }}</span>
                                        @if (user.pack_builder) {
                                        <c-badge color="success" shape="rounded-pill" class="ms-2"
                                            [title]="'user_tree.pack_builder_hint' | translate">
                                            {{ 'user_tree.pack_builder' | translate }}
                                        </c-badge>
                                        }
                                    </td>

                                    <td class="text-center fw-medium">{{ user.organization_volume }}</td>

                                    <td class="text-center">
                                        <span class="fw-medium">{{ user.volume_contribution }}</span>
                                        @if (user.blocks_branch) {
                                        <c-badge color="secondary" shape="rounded-pill" class="ms-2"
                                            [title]="'user_tree.blocked_hint' | translate">
                                            {{ 'user_tree.blocked' | translate }}
                                        </c-badge>
                                        }
                                    </td>

                                    <td>
                                        @if (user.rank_name) {
                                        <div class="fw-semibold">{{ user.rank_name }}</div>
                                        <div class="small text-body-secondary">
                                            {{ +user.weekly_bonus | number: '1.2-2' }} /
                                            {{ 'user_tree.per_week' | translate }}
                                        </div>
                                        } @else {
                                        <span class="text-body-secondary">—</span>
                                        }
                                    </td>

                                    <td class="text-center">
                                        <span class="fw-medium">{{ user.downline_active }}</span>
                                        <span class="text-body-secondary"> / {{ user.downline_total }}</span>
                                    </td>
                                </tr>
                                }

                                @if (data.sponsored_users.length === 0) {
                                <tr>
                                    <td [attr.colspan]="detailsVisible() ? 8 : 7" class="text-center py-5 text-muted">
                                        {{ 'user_tree.no_sponsored_users' | translate }}
                                    </td>
                                </tr>
                                }
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    @if (data.pagination.total_pages > 1) {
                    <div class="d-flex justify-content-between align-items-center mt-4 px-2">
                        <div class="text-body-secondary small">
                            {{ 'user_tree.showing' | translate }}
                            {{ ((data.pagination.current_page - 1) * data.pagination.per_page) + 1 }}
                            {{ 'user_tree.to' | translate }}
                            {{ Math.min(data.pagination.current_page * data.pagination.per_page,
                            data.pagination.total_count) }}
                            {{ 'user_tree.of' | translate }}
                            {{ data.pagination.total_count }}
                            {{ 'user_tree.entries' | translate }}
                        </div>

                        <nav aria-label="Pagination">
                            <ul class="pagination pagination-sm mb-0">
                                <li class="page-item" [class.disabled]="data.pagination.current_page === 1">
                                    <button class="page-link" (click)="goToPage(data.pagination.current_page - 1)"
                                        [disabled]="data.pagination.current_page === 1">
                                        {{ 'user_tree.previous' | translate }}
                                    </button>
                                </li>

                                @for (page of pageNumbers(); track page) {
                                <li class="page-item" [class.active]="page === data.pagination.current_page">
                                    <button class="page-link" (click)="goToPage(page)">
                                        {{ page }}
                                    </button>
                                </li>
                                }

                                <li class="page-item"
                                    [class.disabled]="data.pagination.current_page === data.pagination.total_pages">
                                    <button class="page-link" (click)="goToPage(data.pagination.current_page + 1)"
                                        [disabled]="data.pagination.current_page === data.pagination.total_pages">
                                        {{ 'user_tree.next' | translate }}
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    }
                    }
                </c-card-body>
            </c-card>
        </c-col>
    </c-row>
</c-container>
